import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import GameCard from '@/components/feature/GameCard';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, games, articles, siteConfig } from '@/mocks/siteData';

const topGames = games.slice(0, 8);
const latestArticles = articles.slice(0, 3);
const featuredGames = games.filter((g) => g.rating >= 4.7).slice(0, 4);

export default function Home() {
  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20atmospheric%20gaming%20background%20with%20abstract%20geometric%20shapes%2C%20deep%20crimson%20and%20dark%20tones%2C%20mysterious%20gaming%20aesthetic%2C%20dramatic%20lighting%20with%20glowing%20particles%2C%20modern%20dark%20tech%20atmosphere%2C%20subtle%20gradient%20texture%2C%20no%20text%2C%20clean%20minimal%20dark%20background&width=1800&height=900&seq=hero-main-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-4xl mx-auto py-20">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-foreground-50 font-bold mb-4 leading-tight">
              {siteConfig.tagline}
            </h1>
            <p className="text-base md:text-lg text-foreground-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              {siteConfig.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/japanese"
                className="px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                立即探索
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 border border-foreground-300/40 text-foreground-200 font-medium rounded-md hover:bg-foreground-50/10 hover:border-foreground-300/60 transition-all whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { label: '游戏总数', value: `${siteConfig.stats.totalGames}+`, icon: 'ri-gamepad-line' },
                { label: '累计下载', value: `${(siteConfig.stats.totalDownloads / 10000).toFixed(0)}万+`, icon: 'ri-download-2-line' },
                { label: '活跃用户', value: `${(siteConfig.stats.activeUsers / 10000).toFixed(0)}万+`, icon: 'ri-user-line' },
                { label: '用户好评', value: `${siteConfig.stats.satisfaction}%`, icon: 'ri-heart-line' },
              ].map((stat, i) => (
                <div key={i} className="bg-background-50/40 backdrop-blur-sm rounded-lg p-4 border border-foreground-50/10">
                  <span className="w-8 h-8 flex items-center justify-center mx-auto mb-2 text-primary-400">
                    <i className={`${stat.icon} text-xl`}></i>
                  </span>
                  <div className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">{stat.value}</div>
                  <div className="text-xs text-foreground-400 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold mb-3">
                游戏分类
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                多样的游戏分类，精准找到你喜欢的类型，开启属于你的游戏之旅
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-100 rounded-lg p-5 border border-background-300/30 hover:border-primary-500/30 transition-all duration-300 hover:-translate-y-1 text-center cursor-pointer"
                >
                  <span className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-lg bg-primary-500/10 text-primary-400 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl`}></i>
                  </span>
                  <h3 className="text-sm font-medium text-foreground-800 mb-1.5 whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Games */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-1 h-5 bg-primary-500 rounded-full"></span>
                  <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold">
                    编辑精选
                  </h2>
                </div>
                <p className="text-sm text-foreground-500">高分好评，品质保证，精选优质成人游戏</p>
              </div>
              <Link
                to="/category/japanese"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {featuredGames.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          </div>
        </section>

        {/* Popular Games */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-1 h-5 bg-accent-500 rounded-full"></span>
                  <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold">
                    热门游戏
                  </h2>
                </div>
                <p className="text-sm text-foreground-500">下载量最高的热门成人游戏，玩家之选</p>
              </div>
              <Link
                to="/category/pc"
                className="hidden sm:flex items-center gap-1 text-sm text-accent-400 hover:text-accent-300 transition-colors whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {topGames.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          </div>
        </section>

        {/* Download Guide */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold mb-3">
                下载指引
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                简单三步，安全下载你心仪的成人游戏
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { step: '01', title: '选择游戏', desc: '浏览游戏分类，找到你感兴趣的游戏，查看游戏详情和玩家评价', icon: 'ri-search-line' },
                { step: '02', title: '安全下载', desc: '点击下载按钮，通过安全通道获取游戏文件，全程无广告骚扰', icon: 'ri-shield-check-line' },
                { step: '03', title: '即刻畅玩', desc: '安装游戏后即可开始体验，如有问题可随时联系客服获取帮助', icon: 'ri-gamepad-line' },
              ].map((item, i) => (
                <div key={i} className="relative bg-background-100 rounded-lg p-6 border border-background-300/30 text-center">
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 w-10 h-10 bg-accent-500 text-background-50 font-heading font-bold text-lg rounded-full flex items-center justify-center">
                    {item.step}
                  </span>
                  <span className="w-14 h-14 flex items-center justify-center mx-auto mt-4 mb-4 text-primary-400">
                    <i className={`${item.icon} text-3xl`}></i>
                  </span>
                  <h3 className="text-base font-medium text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-1 h-5 bg-secondary-500 rounded-full"></span>
                  <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold">
                    最新资讯
                  </h2>
                </div>
                <p className="text-sm text-foreground-500">游戏评测、攻略指南、行业资讯一手掌握</p>
              </div>
              <Link
                to="/article/top-10-japanese-games-2026"
                className="hidden sm:flex items-center gap-1 text-sm text-secondary-400 hover:text-secondary-300 transition-colors whitespace-nowrap"
              >
                更多文章 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-16 md:py-20 px-4 md:px-6 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20elegant%20atmospheric%20background%20with%20subtle%20gradient%2C%20deep%20warm%20crimson%20and%20charcoal%20tones%2C%20abstract%20flowing%20shapes%2C%20luxurious%20dark%20aesthetic%2C%20no%20text%2C%20clean%20professional%20dark%20backdrop&width=1800&height=600&seq=cta-section-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50/95 via-background-50/80 to-background-50/95"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold mb-3">
              准备好开始你的游戏之旅了吗？
            </h2>
            <p className="text-sm text-foreground-500 mb-6">
              加入{siteConfig.stats.activeUsers / 10000}万活跃玩家，发现更多优质成人游戏
            </p>
            <Link
              to="/category/japanese"
              className="inline-flex px-8 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              立即探索游戏库
            </Link>
          </div>
        </section>
      </main>

      <Footer />

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: siteConfig.name,
            url: siteConfig.url,
            description: siteConfig.description,
            potentialAction: {
              '@type': 'SearchAction',
              target: `${siteConfig.url}/search?q={search_term_string}`,
              'query-input': 'required name=search_term_string',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: siteConfig.name,
            url: siteConfig.url,
            description: siteConfig.description,
            email: siteConfig.email,
            telephone: siteConfig.phone,
            address: {
              '@type': 'PostalAddress',
              streetAddress: siteConfig.address,
              addressLocality: '深圳',
              addressRegion: '广东',
              addressCountry: 'CN',
            },
          }),
        }}
      />
    </>
  );
}